import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, CheckCircle2 } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", company: "", phone: "", date: "", time: "", message: "" };

const inputCls =
    "w-full rounded-xl border border-[#E2E8F4] bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-ember focus:ring-2 focus:ring-ember/20";

const BookDemoModal = ({ open, onOpenChange, product = "" }) => {
    const [form, setForm] = useState(EMPTY);
    const [submitted, setSubmitted] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim()) {
            toast.error("Please add your name and work email.");
            return;
        }
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-requests`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ ...form, product }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const d = data.detail;
                const msg = Array.isArray(d)
                    ? d.map((x) => (x.msg || "").replace("Value error, ", "")).join(" ")
                    : typeof d === "string"
                      ? d
                      : "Could not send the request. Please try again.";
                throw new Error(msg);
            }
            setSubmitted(true);
            toast.success("Demo request received — we'll confirm your slot shortly.");
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleClose = (next) => {
        onOpenChange(next);
        if (!next) {
            setTimeout(() => {
                setSubmitted(false);
                setForm(EMPTY);
            }, 300);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                data-testid="book-demo-modal"
                className="max-h-[90vh] overflow-y-auto border-[#E2E8F4] bg-bone sm:max-w-lg"
            >
                {submitted ? (
                    <div className="flex flex-col items-center py-8 text-center" data-testid="book-demo-confirmation">
                        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-ember/10">
                            <CheckCircle2 className="h-8 w-8 text-ember" />
                        </span>
                        <h3 className="mt-5 font-serif text-2xl font-semibold text-ink">
                            You're booked in, {form.name.split(" ")[0]}.
                        </h3>
                        <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink/60">
                            Our team will confirm your {product ? `${product} ` : ""}demo
                            {form.date ? ` for ${form.date}${form.time ? ` at ${form.time}` : ""}` : ""} on{" "}
                            <span className="font-medium text-ink">{form.email}</span> within one business day.
                        </p>
                        <button
                            data-testid="book-demo-done-button"
                            onClick={() => handleClose(false)}
                            className="mt-7 rounded-full bg-ember px-8 py-3 text-sm font-semibold text-bone transition-colors hover:bg-ember-dark"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ember/10">
                                <CalendarCheck className="h-5 w-5 text-ember" />
                            </span>
                            <DialogTitle className="font-serif text-2xl font-semibold text-ink">
                                Book a demo{product ? ` — ${product}` : ""}
                            </DialogTitle>
                            <DialogDescription className="text-sm text-ink/60">
                                Thirty focused minutes with a product specialist. Tell us where to send the invite.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="mt-2 space-y-4" data-testid="book-demo-form">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <input
                                    data-testid="demo-name-input"
                                    className={inputCls}
                                    placeholder="Full name *"
                                    value={form.name}
                                    onChange={set("name")}
                                    required
                                />
                                <input
                                    data-testid="demo-email-input"
                                    type="email"
                                    className={inputCls}
                                    placeholder="Work email *"
                                    value={form.email}
                                    onChange={set("email")}
                                    required
                                />
                                <input
                                    data-testid="demo-company-input"
                                    className={inputCls}
                                    placeholder="Company / institution"
                                    value={form.company}
                                    onChange={set("company")}
                                />
                                <input
                                    data-testid="demo-phone-input"
                                    type="tel"
                                    className={inputCls}
                                    placeholder="Phone"
                                    value={form.phone}
                                    onChange={set("phone")}
                                />
                                <input
                                    data-testid="demo-date-input"
                                    type="date"
                                    className={inputCls}
                                    value={form.date}
                                    onChange={set("date")}
                                />
                                <input
                                    data-testid="demo-time-input"
                                    type="time"
                                    className={inputCls}
                                    value={form.time}
                                    onChange={set("time")}
                                />
                            </div>
                            <textarea
                                data-testid="demo-message-input"
                                rows={3}
                                className={`${inputCls} resize-none`}
                                placeholder="What would you like to see in the demo?"
                                value={form.message}
                                onChange={set("message")}
                            />
                            <button
                                type="submit"
                                data-testid="book-demo-submit-button"
                                className="w-full rounded-full bg-ember py-3.5 text-sm font-semibold text-bone shadow-[0_8px_20px_rgba(30,79,163,0.35)] transition-colors hover:bg-ember-dark"
                            >
                                Confirm demo request
                            </button>
                            <p className="text-center text-xs text-ink/40">
                                No commitment — just a walkthrough tailored to your goals.
                            </p>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default BookDemoModal;
